import React, { useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

const AdminHelpPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('faqs');
  const [faqs, setFaqs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', sort_order: 0 });
  const [videoForm, setVideoForm] = useState({ title: '', description: '', video_url: '', category: 'genel', sort_order: 0 });
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const token = user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [faqRes, vidRes] = await Promise.all([
        fetch(`${API}/api/help/admin/faqs`, { headers }),
        fetch(`${API}/api/help/admin/videos`, { headers })
      ]);
      if (faqRes.ok) setFaqs(await faqRes.json());
      if (vidRes.ok) setVideos(await vidRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // ====== FAQ CRUD ======
  const saveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) return alert('Soru ve cevap zorunludur');
    try {
      const url = editingFaq ? `${API}/api/help/admin/faqs/${editingFaq}` : `${API}/api/help/admin/faqs`;
      const method = editingFaq ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(faqForm) });
      if (res.ok) {
        setFaqForm({ question: '', answer: '', sort_order: 0 });
        setEditingFaq(null);
        fetchAll();
      }
    } catch (e) { console.error(e); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/api/help/admin/faqs/${id}`, { method: 'DELETE', headers });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const toggleFaq = async (faq) => {
    try {
      await fetch(`${API}/api/help/admin/faqs/${faq.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ is_active: !faq.is_active })
      });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  // ====== VIDEO CRUD ======
  const saveVideo = async () => {
    if (!videoForm.title.trim()) return alert('Video başlığı zorunludur');

    if (uploadMode === 'file' && videoFile) {
      setUploading(true);
      setUploadProgress(0);
      try {
        const formData = new FormData();
        formData.append('video', videoFile);
        formData.append('title', videoForm.title);
        formData.append('description', videoForm.description);
        formData.append('category', videoForm.category);
        formData.append('sort_order', videoForm.sort_order);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/help/admin/videos/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };

        xhr.onload = () => {
          setUploading(false);
          if (xhr.status === 200) {
            setVideoForm({ title: '', description: '', video_url: '', category: 'genel', sort_order: 0 });
            setVideoFile(null);
            setEditingVideo(null);
            fetchAll();
          } else {
            alert('Video yüklenemedi');
          }
        };

        xhr.onerror = () => { setUploading(false); alert('Video yüklenemedi'); };
        xhr.send(formData);
        return;
      } catch (e) { setUploading(false); console.error(e); return; }
    }

    if (uploadMode === 'url' && !videoForm.video_url.trim()) return alert('Video URL gerekli');

    try {
      const url = editingVideo ? `${API}/api/help/admin/videos/${editingVideo}` : `${API}/api/help/admin/videos`;
      const method = editingVideo ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(videoForm) });
      if (res.ok) {
        setVideoForm({ title: '', description: '', video_url: '', category: 'genel', sort_order: 0 });
        setEditingVideo(null);
        fetchAll();
      }
    } catch (e) { console.error(e); }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm('Bu videoyu silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/api/help/admin/videos/${id}`, { method: 'DELETE', headers });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const toggleVideo = async (video) => {
    try {
      await fetch(`${API}/api/help/admin/videos/${video.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ is_active: !video.is_active })
      });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  if (user?.role !== 'admin' && user?.rol !== 'admin') {
    return <div className="p-8 text-center text-red-600">Yetkiniz yok</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-blue-900">Yardım Merkezi Yönetimi</h1>
          <p className="text-sm text-blue-500 mt-1">SSS ve kullanım videolarını buradan yönetebilirsiniz</p>
        </div>
        <button onClick={() => window.open('/yardim', '_blank')} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-100 transition border border-blue-200">
          Önizleme
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl p-1.5 shadow-sm border border-blue-100">
        <button onClick={() => setActiveTab('faqs')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${activeTab === 'faqs' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'}`}>
          Sık Sorulan Sorular ({faqs.length})
        </button>
        <button onClick={() => setActiveTab('videos')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${activeTab === 'videos' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-700 hover:bg-blue-50'}`}>
          Kullanım Videoları ({videos.length})
        </button>
      </div>

      {/* FAQ Management */}
      {activeTab === 'faqs' && (
        <div>
          {/* Form */}
          <div className="bg-white rounded-xl border border-blue-100 shadow-sm p-5 mb-6">
            <h3 className="text-sm font-bold text-blue-900 mb-3">{editingFaq ? 'SSS Düzenle' : 'Yeni SSS Ekle'}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Soru *</label>
                <input
                  type="text"
                  value={faqForm.question}
                  onChange={e => setFaqForm({ ...faqForm, question: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  placeholder="Sık sorulan soruyu yazın..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Cevap *</label>
                <textarea
                  value={faqForm.answer}
                  onChange={e => setFaqForm({ ...faqForm, answer: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  placeholder="Cevabı yazın..."
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Sıralama</label>
                  <input
                    type="number"
                    value={faqForm.sort_order}
                    onChange={e => setFaqForm({ ...faqForm, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div className="flex gap-2 ml-auto mt-4">
                  {editingFaq && (
                    <button onClick={() => { setEditingFaq(null); setFaqForm({ question: '', answer: '', sort_order: 0 }); }} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition">
                      İptal
                    </button>
                  )}
                  <button onClick={saveFaq} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm">
                    {editingFaq ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ List */}
          <div className="space-y-2">
            {faqs.map((faq, idx) => (
              <div key={faq.id} className={`bg-white rounded-xl border shadow-sm p-4 flex items-start gap-3 ${faq.is_active ? 'border-blue-100' : 'border-gray-200 opacity-60'}`}>
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold mt-0.5">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-blue-900 text-sm">{faq.question}</div>
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">{faq.answer}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => toggleFaq(faq)} className={`p-1.5 rounded-lg text-xs font-semibold transition ${faq.is_active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`} title={faq.is_active ? 'Pasife al' : 'Aktif et'}>
                    {faq.is_active ? '✓' : '✗'}
                  </button>
                  <button onClick={() => { setEditingFaq(faq.id); setFaqForm({ question: faq.question, answer: faq.answer, sort_order: faq.sort_order || 0 }); }} className="p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition" title="Düzenle">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" /></svg>
                  </button>
                  <button onClick={() => deleteFaq(faq.id)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition" title="Sil">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                  </button>
                </div>
              </div>
            ))}
            {faqs.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-400 text-sm">Henüz SSS eklenmemiş</div>
            )}
          </div>
        </div>
      )}

      {/* Video Management */}
      {activeTab === 'videos' && (
        <div>
          {/* Form */}
          <div className="bg-white rounded-xl border border-blue-100 shadow-sm p-5 mb-6">
            <h3 className="text-sm font-bold text-blue-900 mb-3">{editingVideo ? 'Video Düzenle' : 'Yeni Video Ekle'}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Video Başlığı *</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={e => setVideoForm({ ...videoForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="Video başlığı..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama</label>
                <textarea
                  value={videoForm.description}
                  onChange={e => setVideoForm({ ...videoForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="Kısa açıklama..."
                />
              </div>

              {/* Upload mode toggle */}
              {!editingVideo && (
                <div className="flex gap-2">
                  <button onClick={() => setUploadMode('url')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${uploadMode === 'url' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    URL ile Ekle
                  </button>
                  <button onClick={() => setUploadMode('file')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${uploadMode === 'file' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    Dosya Yükle
                  </button>
                </div>
              )}

              {uploadMode === 'url' || editingVideo ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Video URL *</label>
                  <input
                    type="text"
                    value={videoForm.video_url}
                    onChange={e => setVideoForm({ ...videoForm, video_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
                    placeholder="https://... veya /videos/dosya.mp4"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Video Dosyası *</label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={e => setVideoFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  {videoFile && <p className="text-xs text-gray-500 mt-1">{videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
                </div>
              )}

              {uploading && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                  <p className="text-xs text-blue-600 font-semibold mt-1">Yükleniyor... %{uploadProgress}</p>
                </div>
              )}

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Kategori</label>
                  <select
                    value={videoForm.category}
                    onChange={e => setVideoForm({ ...videoForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="genel">Genel</option>
                    <option value="panel-kullanim">Panel Kullanımı</option>
                    <option value="siparis">Sipariş İşlemleri</option>
                    <option value="urun">Ürün Yönetimi</option>
                  </select>
                </div>
                <div className="w-28">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Sıralama</label>
                  <input
                    type="number"
                    value={videoForm.sort_order}
                    onChange={e => setVideoForm({ ...videoForm, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                {editingVideo && (
                  <button onClick={() => { setEditingVideo(null); setVideoForm({ title: '', description: '', video_url: '', category: 'genel', sort_order: 0 }); }} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg transition">
                    İptal
                  </button>
                )}
                <button onClick={saveVideo} disabled={uploading} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm disabled:opacity-50">
                  {uploading ? 'Yükleniyor...' : editingVideo ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </div>
          </div>

          {/* Video List */}
          <div className="space-y-2">
            {videos.map((video, idx) => (
              <div key={video.id} className={`bg-white rounded-xl border shadow-sm p-4 flex items-center gap-4 ${video.is_active ? 'border-blue-100' : 'border-gray-200 opacity-60'}`}>
                <div className="w-20 h-14 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-8 h-8 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-blue-900 text-sm">{video.title}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">{video.category || 'genel'}</span>
                    {video.description && <span className="text-xs text-gray-400 truncate">{video.description}</span>}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5 truncate">{video.video_url}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => toggleVideo(video)} className={`p-1.5 rounded-lg text-xs font-semibold transition ${video.is_active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                    {video.is_active ? '✓' : '✗'}
                  </button>
                  <button onClick={() => { setEditingVideo(video.id); setVideoForm({ title: video.title, description: video.description || '', video_url: video.video_url, category: video.category || 'genel', sort_order: video.sort_order || 0 }); setUploadMode('url'); }} className="p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" /></svg>
                  </button>
                  <button onClick={() => deleteVideo(video.id)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                  </button>
                </div>
              </div>
            ))}
            {videos.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-400 text-sm">Henüz video eklenmemiş</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHelpPage;
